import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import API from "@/lib/api";

/**
 * Currently-served-states strip. Fetches config from
 * `GET /api/public/serving-states` so admins can toggle it on/off and
 * edit the state list from the Banners tab without a code push. Renders
 * nothing until the fetch completes to avoid flashing a stale server-side
 * default.
 */
export default function ServingStatesStrip() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await API.get("/public/serving-states");
        if (alive) setCfg(data);
      } catch { /* silent — strip just stays hidden if the endpoint fails */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!cfg || !cfg.enabled || !Array.isArray(cfg.states) || cfg.states.length === 0) {
    return null;
  }

  return (
    <div
      className="w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white border-b border-slate-700"
      data-testid="serving-states-strip"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-2 flex-wrap text-center">
        <MapPin className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden="true" />
        <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
          {cfg.label || "Currently serving"}:
        </span>
        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          {cfg.states.map((s) => (
            <span
              key={s}
              className="inline-flex items-center justify-center w-9 h-6 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-bold tracking-wider"
              data-testid={`serving-state-${s}`}
              translate="no"
            >
              {s}
            </span>
          ))}
        </div>
        {cfg.subtitle && (
          <span className="text-[10px] sm:text-[11px] text-slate-400 hidden sm:inline">
            · {cfg.subtitle}
          </span>
        )}
      </div>
    </div>
  );
}
